import { useState, useEffect, useRef } from 'react'
import { Upload, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Select } from '@/components/ui/select'
import { Modal } from '@/components/ui/modal'
import { Card, CardContent } from '@/components/ui/card'
import type { TaskType } from './CreateTaskModal'
import { TASK_TYPE_OPTIONS, ACCEPTED_IMAGE_TYPES, MAX_IMAGE_SIZE_BYTES } from './CreateTaskModal'

export interface TaskEditData {
  id: number
  title: string
  description: string
  descriptionClient: string
  type: TaskType | ''
  image: File | null
  imageUrl?: string | null
  removeImage?: boolean
  visibleToClient: boolean
  deadline: string
  deadlineVisibleToClient: boolean
  requiresProdTest: boolean
  createdByEmail?: string
}

export interface EditTaskModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: TaskEditData) => void | Promise<void>
  isLoading?: boolean
  task: TaskEditData | null
}

export function EditTaskModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  task,
}: EditTaskModalProps) {
  const [formData, setFormData] = useState<TaskEditData>({
    id: 0,
    title: '',
    description: '',
    descriptionClient: '',
    type: '',
    image: null,
    visibleToClient: true,
    deadline: '',
    deadlineVisibleToClient: true,
    requiresProdTest: false,
  })
  const [imageError, setImageError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Update form data when task prop changes
  useEffect(() => {
    if (task) {
      setFormData({
        id: task.id,
        title: task.title,
        description: task.description,
        descriptionClient: task.descriptionClient ?? '',
        type: task.type ?? '',
        image: null,
        imageUrl: task.imageUrl ?? null,
        removeImage: false,
        visibleToClient: task.visibleToClient ?? true,
        deadline: task.deadline
          ? new Date(task.deadline).toISOString().split('T')[0]
          : '',
        deadlineVisibleToClient: task.deadlineVisibleToClient ?? true,
        requiresProdTest: task.requiresProdTest ?? false,
      })
      setImageError(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [task])

  const handleInputChange = (field: keyof Omit<TaskEditData, 'image' | 'imageUrl' | 'removeImage'>, value: string | TaskType | '' | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const validateImage = (file: File): string | null => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return 'Format non accepté. Utilisez JPG, PNG, GIF ou WebP.'
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      return 'Taille max : 5 Mo.'
    }
    return null
  }

  const processFile = (file: File | undefined) => {
    if (!file) return
    setImageError(null)
    const error = validateImage(file)
    if (error) {
      setImageError(error)
      setFormData((prev) => ({ ...prev, image: null }))
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setFormData((prev) => ({ ...prev, image: file }))
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      setFormData((prev) => ({ ...prev, image: null }))
      return
    }
    processFile(file)
  }

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setFormData((prev) => ({ ...prev, image: null }))
    setImageError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemoveCurrentImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setFormData((prev) => ({ ...prev, imageUrl: null, removeImage: true }))
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    processFile(e.dataTransfer.files?.[0])
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      return
    }
    if (formData.image && validateImage(formData.image)) {
      setImageError(validateImage(formData.image))
      return
    }

    await onSubmit(formData)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Enregistrer
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-2 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Modifier la tâche</h2>
            <p className="text-sm text-gray-500 mt-1">
              Modifiez les informations de cette tâche
            </p>
            {formData.createdByEmail && (
              <p className="text-xs text-gray-400 mt-2">
                Créée par : {formData.createdByEmail}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Titre de la tâche" required>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Ex: Implémenter l'authentification"
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>

          <FormField label="Type (optionnel)">
            <Select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value as TaskType | '')}
              options={TASK_TYPE_OPTIONS}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>

          <FormField label="Description interne (optionnel)" className="md:col-span-2">
            <Textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Note interne, non visible par le client"
              rows={3}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
            />
          </FormField>

          <FormField label="Description visible par le client (optionnel)" className="md:col-span-2">
            <Textarea
              value={formData.descriptionClient}
              onChange={(e) => handleInputChange('descriptionClient', e.target.value)}
              placeholder="Texte affiché au client. Vide = aucune description côté client."
              rows={3}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              Le client ne la voit que si la tâche est visible.
            </p>
          </FormField>

          <FormField label="Échéance (optionnel)">
            <Input
              type="date"
              value={formData.deadline}
              onChange={(e) => handleInputChange('deadline', e.target.value)}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>

          <div className={`flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 h-full ${!formData.deadline ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="flex-1 min-w-0 pr-3">
              <p className="text-sm font-medium text-gray-700">
                Échéance visible par le client
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleInputChange('deadlineVisibleToClient', !formData.deadlineVisibleToClient)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                formData.deadlineVisibleToClient ? 'bg-cyan-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.deadlineVisibleToClient ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 h-full">
            <div className="flex-1 min-w-0 pr-3">
              <p className="text-sm font-medium text-gray-700">
                Nécessite d&apos;être testé en production
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Confirmation requise avant de marquer terminée
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleInputChange('requiresProdTest', !formData.requiresProdTest)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                formData.requiresProdTest ? 'bg-cyan-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.requiresProdTest ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 h-full">
            <div className="flex-1 min-w-0 pr-3">
              <p className="text-sm font-medium text-gray-700">
                Visible par le client
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Le client ne voit pas la description interne
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleInputChange('visibleToClient', !formData.visibleToClient)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                formData.visibleToClient ? 'bg-cyan-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.visibleToClient ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <FormField label="Image (optionnel)" error={imageError ?? undefined} className="md:col-span-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
            onChange={handleImageChange}
            className="sr-only"
            aria-hidden
          />
          <Card
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`
              cursor-pointer transition-colors border-2 border-dashed
              ${isDragging ? 'border-cyan-400 bg-cyan-50/50' : 'border-gray-200 hover:border-cyan-300 hover:bg-gray-50/50'}
              ${formData.image || (formData.imageUrl && !formData.image && !formData.removeImage) ? 'border-solid border-gray-200' : ''}
            `}
          >
            <CardContent className="p-4">
              {formData.image ? (
                <div className="flex items-center gap-3">
                  <div className="relative shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                    <img
                      src={URL.createObjectURL(formData.image)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">{formData.image.name}</p>
                    <p className="text-xs text-gray-500">
                      {(formData.image.size / 1024).toFixed(1)} Ko
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleRemoveImage}
                    className="shrink-0"
                    aria-label="Retirer l'image"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : formData.imageUrl && !formData.image && !formData.removeImage ? (
                <div className="flex items-center gap-3">
                  <div className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                    <img src={formData.imageUrl} alt="Tâche" className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800">Image actuelle</p>
                    <p className="text-xs text-gray-500">Cliquez pour remplacer</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleRemoveCurrentImage}
                    className="shrink-0"
                    aria-label="Supprimer l'image"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="rounded-full bg-cyan-100 p-3 mb-2">
                    <Upload className="h-6 w-6 text-cyan-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">
                    Cliquez ou glissez une image ici
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    JPG, PNG, GIF, WebP. Taille max : 5 Mo.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          </FormField>
        </div>
      </div>
    </Modal>
  )
}

